import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import AuthLayout from '../components/public/AuthLayout';
import TextField from '../components/public/TextField';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get('token') ?? '', [searchParams]);
  const [token, setToken] = useState(tokenFromUrl);
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pwd1 !== pwd2) { setError('Passwords do not match.'); return; }
    if (pwd1.length < 14) { setError('Password must be at least 14 characters.'); return; }
    setLoading(true);
    try {
      await authService.resetPassword({ token, new_password: pwd1 });
      setDone(true);
      setTimeout(() => navigate('/login'), 1800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? 'Unable to reset password.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Pick something memorable but unique. At least 14 characters."
      backHref="/login"
      backLabel="Back to sign in"
    >
      {done ? (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
          Password updated. Redirecting you to sign in…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          {!tokenFromUrl && (
            <TextField
              label="Reset token"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              helperText="Paste the reset token you received."
            />
          )}
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={pwd1}
            onChange={(e) => setPwd1(e.target.value)}
            helperText="At least 14 characters."
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading || !token || !pwd1 || !pwd2}
            className="w-full bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] disabled:opacity-50 transition-colors"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
